import axios from 'axios';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import API_URL from '../config/api';
import AuthLayout from './AuthLayout';
import './AuthLayout.css';

const AuthRegister = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    name: '',
    lastName: '',
    gender: 'Male',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'respondent',
    country: '',
    dateOfBirth: '',
    companyName: '',
    industry: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [selectedRole, setSelectedRole] = useState('respondent');
  const [needsVerification, setNeedsVerification] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [canResend, setCanResend] = useState(true);

  const countries = [
    { code: 'AU', name: 'Australia', flag: '🇦🇺', phoneCode: '+61' },
    { code: 'BR', name: 'Brazil', flag: '🇧🇷', phoneCode: '+55' },
    { code: 'CA', name: 'Canada', flag: '🇨🇦', phoneCode: '+1' },
    { code: 'CN', name: 'China', flag: '🇨🇳', phoneCode: '+86' },
    { code: 'FR', name: 'France', flag: '🇫🇷', phoneCode: '+33' },
    { code: 'DE', name: 'Germany', flag: '🇩🇪', phoneCode: '+49' },
    { code: 'HK', name: 'Hong Kong', flag: '🇭🇰', phoneCode: '+852' },
    { code: 'IN', name: 'India', flag: '🇮🇳', phoneCode: '+91' },
    { code: 'ID', name: 'Indonesia', flag: '🇮🇩', phoneCode: '+62' },
    { code: 'IT', name: 'Italy', flag: '🇮🇹', phoneCode: '+39' },
    { code: 'JP', name: 'Japan', flag: '🇯🇵', phoneCode: '+81' },
    { code: 'MY', name: 'Malaysia', flag: '🇲🇾', phoneCode: '+60' },
    { code: 'MX', name: 'Mexico', flag: '🇲🇽', phoneCode: '+52' },
    { code: 'SG', name: 'Singapore', flag: '🇸🇬', phoneCode: '+65' },
    { code: 'KR', name: 'South Korea', flag: '🇰🇷', phoneCode: '+82' },
    { code: 'ES', name: 'Spain', flag: '🇪🇸', phoneCode: '+34' },
    { code: 'TH', name: 'Thailand', flag: '🇹🇭', phoneCode: '+66' },
    { code: 'AE', name: 'United Arab Emirates', flag: '🇦🇪', phoneCode: '+971' },
    { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', phoneCode: '+44' },
    { code: 'US', name: 'United States', flag: '🇺🇸', phoneCode: '+1' },
    { code: 'VN', name: 'Vietnam', flag: '🇻🇳', phoneCode: '+84' }
  ];

  const industries = [
    'Technology',
    'Healthcare',
    'Finance',
    'Retail',
    'Education',
    'Manufacturing',
    'Marketing',
    'Consulting',
    'Non-profit',
    'Government',
    'Other'
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    setFormData({
      ...formData,
      role: role
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setNeedsVerification(false);
    setResendMessage('');

    try {
      const { confirmPassword, ...registrationData } = formData;
      await axios.post(`${API_URL}/users/register`, registrationData);
      
      setSuccess(true);
      setError('');
    } catch (error) {
      const errorData = error.response?.data;

      if (errorData?.needsVerification) {
        setNeedsVerification(true);
        setUnverifiedEmail(errorData.email || formData.email);
        setError(errorData.message);
      } else if (errorData?.errors && Array.isArray(errorData.errors)) {
        // Handle validation errors
        const errorMessages = errorData.errors.map(err => err.msg).join(', ');
        setError(errorMessages);
      } else {
        setError(errorData?.message || 'Registration failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!unverifiedEmail || !canResend) return;

    setResending(true);
    setResendMessage('');

    try {
      const response = await axios.post(`${API_URL}/users/resend-verification`, {
        email: unverifiedEmail
      });

      setResendMessage(response.data.message);

      // Start 1-minute timer
      setCanResend(false);
      setResendTimer(60);
    } catch (error) {
      setResendMessage(error.response?.data?.message || 'Failed to resend verification email');
    } finally {
      setResending(false);
    }
  };

  // Timer effect for resend cooldown
  React.useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (resendTimer === 0 && !canResend) {
      setCanResend(true);
    }
  }, [resendTimer, canResend]);

  if (success) {
    return (
      <AuthLayout>
        <div className="register-container">
          <div className="success-message">
            <h1 className="success-title">Registration Successful!</h1>
            <p className="success-text">
              Your account has been created. Let's complete your profile to get started!
            </p>
            <Link to="/survey-profiler/all" className="btn btn-primary btn-lg">
              Start Profile Questions
            </Link>
            <p style={{ marginTop: '15px', fontSize: '14px', color: '#666' }}>
              Or <Link to="/auth/login" style={{ color: '#007bff' }}>login</Link> if you already have an account
            </p>
          </div>
        </div>
      </AuthLayout>
    );
  }



  return (
    <AuthLayout>
      <div className="register-container">
        <div className="register-header">
          <h1 className="register-title">
            Create New Account!
          </h1>
        </div>
        
        <form onSubmit={handleSubmit} className="register-form">
          {error && (
            <div className="alert alert-danger">
              {error}
              {needsVerification && (
                <div className="verification-help" style={{ marginTop: '15px', padding: '10px', backgroundColor: '#fff3cd', borderRadius: '4px' }}>
                  <p style={{ margin: '0 0 10px 0', fontSize: '14px' }}>
                    This email is already registered but not verified. Would you like to receive a new verification email?
                  </p>
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    className="btn btn-sm btn-warning"
                    disabled={resending || !canResend}
                    style={{ fontSize: '13px', padding: '6px 12px' }}
                  >
                    {resending ? 'Sending...' : !canResend ? `Resend in ${resendTimer}s` : 'Resend Verification Email'}
                  </button>
                  {resendMessage && (
                    <p style={{ margin: '10px 0 0 0', fontSize: '13px', color: resendMessage.includes('sent') ? '#28a745' : '#dc3545' }}>
                      {resendMessage}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          
          <div className="form-row">
            <div className="form-group half-width">
              <label htmlFor="firstName" className="form-label">First Name</label>
              <input
                type="text"
                id="firstName"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="form-input"
                placeholder="First Name"
                required
              />
            </div>
            
            <div className="form-group half-width">
              <label htmlFor="lastName" className="form-label">Last Name</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName || ''}
                onChange={handleChange}
                className="form-input"
                placeholder="Last Name"
                required
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group half-width">
              <label htmlFor="gender" className="form-label">Gender</label>
              <select
                id="gender"
                name="gender"
                value={formData.gender || 'Male'}
                onChange={handleChange}
                className="form-input form-select"
                required
              >
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            
            <div className="form-group half-width">
              <label htmlFor="dateOfBirth" className="form-label">Date of Birth</label>
              <input
                type="date"
                id="dateOfBirth"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
                className="form-input"
                placeholder="dd-mm-yyyy"
                required
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email" className="form-label">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="form-input"
                placeholder="Enter your email address"
                required
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="country" className="form-label">Country</label>
              <select
                id="country"
                name="country"
                value={formData.country}
                onChange={handleChange}
                className="form-input form-select"
                required
              >
                <option value="">Select your country</option>
                {countries.map(country => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="password" className="form-label">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="form-input"
                placeholder="••••••••••••••••••••"
                required
                minLength="6"
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <p className="terms-text">
                By registering you agree to the <a href="#" className="terms-link">Terms & Conditions</a>
              </p>
            </div>
          </div>
          
          <button 
            type="submit" 
            className="btn btn-signup"
            disabled={loading}
          >
            {loading ? 'Creating Account...' : 'Sign Up'}
          </button>
        </form>
        
        <div className="register-footer">
          <p>
            Already have an account? 
            <Link to="/auth/login" className="login-link">
              Login
            </Link>
          </p>
        </div>
      </div>
    </AuthLayout>
  );
};

export default AuthRegister;
